import { useState, useRef, useCallback, useEffect } from "react";
import { IconWarning, SimulatorsEdit, SimulatorsTrash, ClearMM } from "~misc/icons";

const pageSize = 10;
const pastColor = "#FF2E00";
const activeColor = "#01054C";

const WorkRow = ({ item, statusOptions, onEdit, checked, onToggle, edit, onRowEdit, onRowReset }) => {
  const originalStatusLabel = statusOptions.find((o) => o.value === item.status)?.label ?? item.status;
  const originalVolume = item.volume;

  const [volume, setVolume] = useState(edit?.volume ?? originalVolume);
  const [status, setStatus] = useState(edit?.status ?? originalStatusLabel);

  const isVolumeEdited = volume !== originalVolume;
  const isStatusEdited = status !== originalStatusLabel;
  const isEdited = isVolumeEdited || isStatusEdited;

  const commit = (next) => {
    if (next.volume !== originalVolume || next.status !== originalStatusLabel) {
      onRowEdit?.(item.id, next);
    } else {
      onRowReset?.(item.id);
    }
  };

  const handleVolumeChange = (e) => {
    const next = Number(e.target.value);
    setVolume(next);
    commit({ volume: next, status });
  };

  const handleStatusChange = (e) => {
    const next = e.target.value;
    setStatus(next);
    commit({ volume, status: next });
  };

  const handleReset = () => {
    setVolume(originalVolume);
    setStatus(originalStatusLabel);
    onRowReset?.(item.id);
  };

  const editedClass = "border border-[#034EA2] bg-[#E2F0FF] text-[#034EA2]";
  const defaultVolumeClass = "border border-gray-200 bg-white text-[#01054C]";
  const defaultStatusClass = "border border-gray-200 bg-white text-[#1C2B58]";

  return (
    <tr className="border-b border-[#D5D7E1] last:border-none bg-[#E9EAEF]">
      <td className="pl-6 pr-2 py-3 w-12">
        <input
          type="checkbox"
          checked={checked}
          onChange={onToggle}
          className="w-5 h-5 accent-[#01054C] rounded"
        />
      </td>
      <td className="px-3 py-3 text-sm font-bold text-[#01054C]">{item.clienteCodigo}</td>
      <td className="px-3 py-3 text-sm text-[#1C2B58]">{item.produto}</td>
      <td className="px-3 py-3 text-sm text-[#1C2B58]">{item.sku}</td>
      <td className="px-3 py-3">
        <input
          type="number"
          value={volume}
          onChange={handleVolumeChange}
          className={`w-20 rounded-md px-2 py-1.5 text-sm text-center font-semibold ${
            isVolumeEdited ? editedClass : defaultVolumeClass
          }`}
        />
      </td>
      <td className="px-3 py-3">
        <input
          type="text"
          value={status}
          onChange={handleStatusChange}
          className={`rounded-md px-3 py-1.5 text-sm min-w-[120px] ${
            isStatusEdited ? editedClass : defaultStatusClass
          }`}
        />
      </td>
      <td className="pr-6 py-3">
        <div className="flex gap-2 justify-end items-center">
          {isEdited && (
            <button
              type="button"
              onClick={handleReset}
              aria-label="Limpar edição"
              className="w-9 h-9 rounded-full bg-[#034EA2] flex items-center justify-center hover:opacity-90 transition"
            >
              <span className="scale-75">
                <ClearMM />
              </span>
            </button>
          )}
          <button type="button" className="hover:opacity-80" onClick={onEdit}>
            <SimulatorsEdit size="36" />
          </button>
          <button type="button" className="hover:opacity-80">
            <SimulatorsTrash size="36" />
          </button>
        </div>
      </td>
    </tr>
  );
};

const WorkItem = ({ work, statusOptions, onEdit, selected, onToggle, edits, onRowEdit, onRowReset }) => {
  const [expanded, setExpanded] = useState(true);
  const color = work.isPast ? pastColor : activeColor;

  return (
    <div
      className="mb-4 rounded-2xl overflow-hidden bg-white"
      style={{ border: `2px solid ${color}` }}
    >
      <div
        className="grid grid-cols-3 px-8 py-3 text-white"
        style={{ backgroundColor: color }}
      >
        <div>
          <p className="text-xs font-semibold ">Próxima Negociação:</p>
          <p className="text-sm font-bold">{work.proximaNegociacao}</p>
        </div>
        <div className="text-center">
          <p className="text-xs font-semibold ">Volume Total:</p>
          <p className="text-sm font-bold">{work.volumeTotal}</p>
        </div>
        <div className="text-right">
          <div className="flex items-center justify-end gap-1 mb-0.5">
            <p className="text-xs font-semibold ">Último Status:</p>
            {work.isPast && <IconWarning fill="white" width="16" height="14" />}
          </div>
          <p className="text-sm font-bold">{work.ultimoStatus}</p>
        </div>
      </div>

      <div className="bg-white flex flex-col items-center pt-2 pb-3 gap-1.5">
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="flex items-center gap-2 text-xs text-[#1C2B58] font-semibold"
        >
          {expanded ? "Exibir menos" : "Exibir mais"}
          <svg width="12" height="12" viewBox="0 0 12 12" className={expanded ? "" : "rotate-180"}>
            <path d="M2 8 L6 4 L10 8" stroke="currentColor" strokeWidth="1.5" fill="none" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>
        <div className="w-10 h-1 rounded-full bg-[#D5D7E1]" />
      </div>

      {expanded && (
        <div className="px-4 pb-4">
          <table className="w-full rounded-xl overflow-hidden">
            <thead>
              <tr className="bg-[#D5D7E1]">
              <th className="w-12" />
              <th className="px-3 py-2.5 text-left text-xs font-semibold text-[#1C2B58]">Cód.Cliente</th>
              <th className="px-3 py-2.5 text-left text-xs font-semibold text-[#1C2B58]">Produto</th>
              <th className="px-3 py-2.5 text-left text-xs font-semibold text-[#1C2B58]">SKU</th>
              <th className="px-3 py-2.5 text-left text-xs font-semibold text-[#1C2B58]">Volume</th>
              <th className="px-3 py-2.5 text-left text-xs font-semibold text-[#1C2B58]">Status</th>
              <th className="w-28" />
            </tr>
          </thead>
          <tbody>
            {work.items.map((item) => (
              <WorkRow
                key={item.id}
                item={item}
                statusOptions={statusOptions}
                onEdit={onEdit}
                checked={!!selected[item.id]}
                onToggle={() => onToggle(item.id)}
                edit={edits?.[item.id]}
                onRowEdit={onRowEdit}
                onRowReset={onRowReset}
              />
            ))}
          </tbody>
        </table>
        </div>
      )}
    </div>
  );
};

const NegotiationsWorksList = ({
  works = [],
  statusOptions = [],
  selected = {},
  onToggle,
  onSelectAll,
  onEditRow,
  edits = {},
  onRowEdit,
  onRowReset,
}) => {
  const [visibleCount, setVisibleCount] = useState(pageSize);
  const sentinelRef = useRef(null);
  const hasPast = works.some((w) => w.isPast);
  const visible = works.slice(0, visibleCount);

  const allIds = works.flatMap((w) => w.items.map((item) => item.id));
  const allSelected = allIds.length > 0 && allIds.every((id) => !!selected[id]);

  const loadMore = useCallback(() => {
    setVisibleCount((c) => Math.min(c + pageSize, works.length));
  }, [works.length]);

  useEffect(() => {
    const el = sentinelRef.current;
    if (!el || visibleCount >= works.length) return;
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) loadMore(); },
      { threshold: 0.1 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [loadMore, visibleCount, works.length]);

  return (
    <div className="container mt-6">
      <div className="flex justify-end items-center mb-2 gap-2">
        <span className="text-sm font-bold text-[#01054C]">Selecionar todas</span>
        <input
          type="checkbox"
          checked={allSelected}
          onChange={() => onSelectAll?.(allIds, !allSelected)}
          className="w-5 h-5 accent-[#01054C] rounded"
        />
      </div>
      {hasPast && (
        <div className="relative mb-4 ml-1">
          <div className="absolute left-0 top-3 bottom-3 w-1.5 bg-[#EE4F2C] rounded-full" style={{ zIndex: 0 }} />
          <div className="relative flex items-center justify-center gap-8 bg-[#FBEAEA] rounded-xl px-8 py-4 ml-1" style={{ zIndex: 1 }}>
            <IconWarning fill="#EE4F2C" width="40" height="36" />
            <p className="text-sm font-bold text-[#01054C]">
              Existem Obras com Negociação no Passado!
            </p>
          </div>
        </div>
      )}
      {visible.map((work) => (
        <WorkItem
          key={work.id}
          work={work}
          statusOptions={statusOptions}
          onEdit={onEditRow}
          selected={selected}
          onToggle={onToggle}
          edits={edits}
          onRowEdit={onRowEdit}
          onRowReset={onRowReset}
        />
      ))}
      {visibleCount < works.length && <div ref={sentinelRef} className="h-2" />}
    </div>
  );
};

export default NegotiationsWorksList;
